import { useCallback } from 'react';
import { Element, ElementType } from '@ritabygglov/core';
import { AppState, SelectionModes, SelectionMode } from '../../../types';
import { resolveSelectionId } from '../../../helpers/groupHelpers';
import { toggleInArray, ensureSelected } from '../utils/selectionUtils';

export const calculateNewSelection = (
  elementId: string,
  currentSelection: string[],
  isShiftPressed: boolean
): string[] => {
  if (isShiftPressed) {
    return toggleInArray(currentSelection, elementId);
  }
  return ensureSelected(currentSelection, elementId);
};

export const getElementsFromIds = (
  elementIds: string[],
  elements: Record<string, Element>
): Element[] => {
  return elementIds
    .map((id) => elements[id])
    .filter((el): el is Element => el !== undefined);
};

export const createSelectionMode = (
  element: Element,
  currentMode: SelectionMode
): SelectionMode => {
  // Selecting a child while GroupEditing stays inside that mode (preserving
  // groupId) rather than falling back to a plain Selected state — the caller
  // (MouseEvents.onMouseDown) only reaches here for clicks confirmed to be
  // within the currently-open group's scope.
  const isGroupEditing = currentMode.type === SelectionModes.GroupEditing;
  return {
    ...currentMode,
    type: isGroupEditing
      ? SelectionModes.GroupEditing
      : SelectionModes.Selected,
    elementType: element.type,
    lineType:
      element.type === ElementType.Polyline ? element.lineType : undefined,
  };
};

export interface ElementSelectionDeps {
  appState: AppState;
  selectedElements: string[];
  setSelectedElements: React.Dispatch<React.SetStateAction<string[]>>;
  selectionMode: SelectionMode;
  setSelectionMode: React.Dispatch<React.SetStateAction<SelectionMode>>;
}

export const useElementSelection = (hooks: ElementSelectionDeps) => {
  const {
    appState,
    selectedElements,
    setSelectedElements,
    selectionMode,
    setSelectionMode,
  } = hooks;

  const onElementClick = useCallback(
    (elementId: string, isShiftPressed: boolean): Element[] | null => {
      if (!appState.elements[elementId]) {
        return null;
      }

      const activeGroupId =
        selectionMode.type === SelectionModes.GroupEditing
          ? selectionMode.groupId
          : undefined;
      const resolvedId = resolveSelectionId(
        elementId,
        appState.elements,
        activeGroupId
      );

      const newSelectionIds = calculateNewSelection(
        resolvedId,
        selectedElements,
        isShiftPressed
      );

      if (newSelectionIds.length === 0) {
        return null;
      }

      const elements = getElementsFromIds(newSelectionIds, appState.elements);

      setSelectedElements(newSelectionIds);

      const firstElement = elements[0];
      if (firstElement) {
        setSelectionMode(createSelectionMode(firstElement, selectionMode));
      }

      return elements;
    },
    [
      appState.elements,
      selectedElements,
      setSelectedElements,
      selectionMode,
      setSelectionMode,
    ]
  );

  return {
    onElementClick,
  };
};
